import React, { useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { getBoreholeDetails, clearBoreholesError } from '../../../../redux/Slices/boreholesSlice';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { FaArrowLeft, FaArrowRight, FaFilePdf } from 'react-icons/fa';
import { BASE_URL } from '../../../../shared/utils.js/utils';

const BoreholeDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { details, isLoading, error } = useSelector((state) => state.boreholes);
  const { t, i18n } = useTranslation('global');
  const isRTL = i18n.language === 'ar';
  const textAlign = isRTL ? 'right' : 'left';
  const dir = isRTL ? 'rtl' : 'ltr';

  useEffect(() => {
    if (id) {
      dispatch(getBoreholeDetails(id));
    }
  }, [dispatch, id]);

  useEffect(() => {
    if (error) {
      const safeErrorText =
        (typeof error === 'string' && error) ||
        error?.message ||
        error?.data?.message ||
        error?.response?.data?.message ||
        error?.name_en ||
        error?.name_ar ||
        error?.name ||
        t('common.error', { defaultValue: 'حدث خطأ غير متوقع' });
      toast.error(String(safeErrorText), { rtl: isRTL });
      setTimeout(() => dispatch(clearBoreholesError()), 3000);
    }
  }, [error, dispatch, isRTL, t]);

  const handleBack = () => {
    navigate('/boreholes');
  };

  if (isLoading || !details) {
    return (
      <div style={{
        padding: '30px',
        backgroundColor: '#14263b',
        minHeight: 'calc(100vh - 80px)',
        color: 'white',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center'
      }}>
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">{t('loading', { defaultValue: 'جاري التحميل...' })}</span>
        </div>
      </div>
    );
  }

  const layers = Array.isArray(details.layers) ? details.layers : [];

  const getDisplayValue = (value, fallback = '-') => {
    if (value === null || value === undefined || value === '') return fallback;
    if (typeof value === 'object') {
      return (
        value.name_en ||
        value.name_ar ||
        value.name ||
        value.title ||
        value.code ||
        value.borehole_number ||
        value.project_name ||
        (value.id !== undefined ? String(value.id) : fallback)
      );
    }
    return String(value);
  };

  const formatDate = (value) => {
    if (!value) return '-';
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? getDisplayValue(value) : d.toLocaleString('en-US');
  };

  // Function to determine layer color dynamically based on legend or soil description
  const getLayerColor = (legend) => {
    const lg = legend ? legend.toLowerCase() : '';
    if (lg.includes('sand') || lg.includes('رمل')) return '#c2b280';
    if (lg.includes('clay') || lg.includes('طين')) return '#6e5a4e';
    if (lg.includes('rock') || lg.includes('صخر')) return '#888c8d';
    if (lg.includes('silt') || lg.includes('طمي')) return '#a59371';
    if (lg.includes('gravel') || lg.includes('زلط') || lg.includes('حصى')) return '#9aa0a6';
    return '#8a7d65'; // default soil
  };

  const getLayerThickness = (layer) => {
    const direct = Number(layer?.thickness);
    if (Number.isFinite(direct) && direct > 0) return direct;
    const start = Number(layer?.start_depth);
    const end = Number(layer?.end_depth);
    if (Number.isFinite(start) && Number.isFinite(end) && end >= start) {
      return end - start;
    }
    return 0;
  };

  return (
    <div style={{ padding: '30px', backgroundColor: '#14263b', minHeight: 'calc(100vh - 80px)', color: 'white', direction: dir, textAlign }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '30px' }}>
        <button onClick={handleBack} style={{ backgroundColor: '#2f3644', color: 'white', border: 'none', padding: '10px 15px', borderRadius: '6px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px' }}>
          {isRTL ? <FaArrowRight /> : <FaArrowLeft />}
          {t('boreholes.back', { defaultValue: 'رجوع' })}
        </button>
        <h1 style={{ fontSize: '24px', fontWeight: 'bold', margin: 0, color: 'white' }}>
          {t('boreholes.details.title', { defaultValue: 'تفاصيل مسطرة الجسة' })}
        </h1>
        {/* <a 
            href={`${BASE_URL}/boreholes/${id}/pdf`}
            target="_blank"
            rel="noopener noreferrer"
            title={t('btns.pdf', { defaultValue: 'تقرير PDF' })}
            style={{ backgroundColor: '#AC2000', color: 'white', border: 'none', padding: '10px 15px', borderRadius: '6px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px', textDecoration: 'none' }}
        >
            <FaFilePdf />
            {t('boreholes.print', { defaultValue: 'طباعة التقرير' })}
        </a> */}
        <div></div>
      </div>

      {/* Info Card */}
      <div style={{ backgroundColor: '#1b3350', border: '1px solid #333', borderRadius: '12px', padding: '20px', marginBottom: '30px' }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '20px' }}>
          <div>
            <p style={{ color: '#aaa', margin: '0 0 5px 0', fontSize: '14px' }}>{t('boreholes.project_name', { defaultValue: 'اسم المشروع' })}</p>
            <p style={{ fontSize: '16px', fontWeight: 'bold', margin: 0 }}>{getDisplayValue(details.project_name || details.project)}</p>
          </div>
          <div>
            <p style={{ color: '#aaa', margin: '0 0 5px 0', fontSize: '14px' }}>{t('boreholes.customer_or_contractor', { defaultValue: 'العميل أو المقاول' })}</p>
            <p style={{ fontSize: '16px', fontWeight: 'bold', margin: 0 }}>{getDisplayValue(details.owner)}</p>
          </div>
          {/* <div>
            <p style={{ color: '#aaa', margin: '0 0 5px 0', fontSize: '14px' }}>{t('boreholes.main_contractor', { defaultValue: 'المقاول الرئيسي' })}</p>
            <p style={{ fontSize: '16px', fontWeight: 'bold', margin: 0 }}>{getDisplayValue(details.main_contractor || details.contractor)}</p>
          </div> */}
          <div>
            <p style={{ color: '#aaa', margin: '0 0 5px 0', fontSize: '14px' }}>{t('boreholes.location', { defaultValue: 'الموقع' })}</p>
            <p style={{ fontSize: '16px', fontWeight: 'bold', margin: 0 }}>{getDisplayValue(details.location)}</p>
          </div>
          <div>
            <p style={{ color: '#aaa', margin: '0 0 5px 0', fontSize: '14px' }}>{t('boreholes.consultant', { defaultValue: 'الاستشاري' })}</p>
            <p style={{ fontSize: '16px', fontWeight: 'bold', margin: 0 }}>{getDisplayValue(details.consultant)}</p>
          </div>
          <div>
            <p style={{ color: '#aaa', margin: '0 0 5px 0', fontSize: '14px' }}>{t('boreholes.boring_method', { defaultValue: 'طريقة الحفر' })}</p>
            <p style={{ fontSize: '16px', fontWeight: 'bold', margin: 0 }}>{getDisplayValue(details.boring_method)}</p>
          </div>
          <div>
            <p style={{ color: '#aaa', margin: '0 0 5px 0', fontSize: '14px' }}>{t('boreholes.diameter', { defaultValue: 'القطر' })}</p>
            <p style={{ fontSize: '16px', fontWeight: 'bold', margin: 0 }}>{getDisplayValue(details.diameter)}</p>
          </div>
          <div>
            <p style={{ color: '#aaa', margin: '0 0 5px 0', fontSize: '14px' }}>{t('boreholes.groundwater_level', { defaultValue: 'منسوب المياه' })}</p>
            <p style={{ fontSize: '16px', fontWeight: 'bold', margin: 0 }}>{getDisplayValue(details.groundwater_level)}</p>
          </div>
          <div>
            <p style={{ color: '#aaa', margin: '0 0 5px 0', fontSize: '14px' }}>{t('boreholes.ace_ref_no', { defaultValue: 'رقم المرجع' })}</p>
            <p style={{ fontSize: '16px', fontWeight: 'bold', margin: 0 }}>{getDisplayValue(details.ace_ref_no)}</p>
          </div>
          <div>
            <p style={{ color: '#aaa', margin: '0 0 5px 0', fontSize: '14px' }}>{t('boreholes.borehole_number', { defaultValue: 'رقم الجسة' })}</p>
            <p style={{ fontSize: '16px', fontWeight: 'bold', margin: 0 }}>{getDisplayValue(details.borehole_number)}</p>
          </div>
          <div>
            <p style={{ color: '#aaa', margin: '0 0 5px 0', fontSize: '14px' }}>{t('boreholes.total_depth', { defaultValue: 'العمق الكلي' })}</p>
            <p style={{ fontSize: '16px', fontWeight: 'bold', margin: 0 }}>
              {details.total_depth !== null && details.total_depth !== undefined && details.total_depth !== ''
                ? `${getDisplayValue(details.total_depth, '')}m`
                : '-'}
            </p>
          </div>
          <div>
            <p style={{ color: '#aaa', margin: '0 0 5px 0', fontSize: '14px' }}>{t('boreholes.report_date', { defaultValue: 'تاريخ التقرير' })}</p>
            <p style={{ fontSize: '16px', fontWeight: 'bold', margin: 0 }}>{formatDate(details.report_date)}</p>
          </div>
          <div>
            <p style={{ color: '#aaa', margin: '0 0 5px 0', fontSize: '14px' }}>{t('boreholes.id', { defaultValue: 'رقم السجل' })}</p>
            <p style={{ fontSize: '16px', fontWeight: 'bold', margin: 0 }}>{getDisplayValue(details.id)}</p>
          </div>
          {/* <div>
            <p style={{ color: '#aaa', margin: '0 0 5px 0', fontSize: '14px' }}>{t('boreholes.project_id', { defaultValue: 'رقم المشروع' })}</p>
            <p style={{ fontSize: '16px', fontWeight: 'bold', margin: 0 }}>{getDisplayValue(details.project_id)}</p>
          </div> */}
          {/* <div>
            <p style={{ color: '#aaa', margin: '0 0 5px 0', fontSize: '14px' }}>{t('boreholes.customer_id', { defaultValue: 'رقم العميل' })}</p>
            <p style={{ fontSize: '16px', fontWeight: 'bold', margin: 0 }}>{getDisplayValue(details.customer_id)}</p>
          </div> */}
          {/* <div>
            <p style={{ color: '#aaa', margin: '0 0 5px 0', fontSize: '14px' }}>{t('boreholes.contractor_id', { defaultValue: 'رقم المقاول' })}</p>
            <p style={{ fontSize: '16px', fontWeight: 'bold', margin: 0 }}>{getDisplayValue(details.contractor_id)}</p>
          </div> */}
          <div>
            <p style={{ color: '#aaa', margin: '0 0 5px 0', fontSize: '14px' }}>{t('common.created_at', { defaultValue: 'تاريخ الإنشاء' })}</p>
            <p style={{ fontSize: '16px', fontWeight: 'bold', margin: 0 }}>{formatDate(details.created_at)}</p>
          </div>
          <div>
            <p style={{ color: '#aaa', margin: '0 0 5px 0', fontSize: '14px' }}>{t('common.updated_at', { defaultValue: 'آخر تحديث' })}</p>
            <p style={{ fontSize: '16px', fontWeight: 'bold', margin: 0 }}>{formatDate(details.updated_at)}</p>
          </div>
        </div>
      </div>

      <div style={{ display: 'flex', gap: '30px', flexWrap: 'wrap' }}>
        
        {/* Visual Ruler Sector */}
        {/* <div style={{ flex: '1', minWidth: '300px', backgroundColor: '#1b3350', borderRadius: '12px', border: '1px solid #333', padding: '20px', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <h3 style={{ marginBottom: '30px', borderBottom: '1px solid #333', paddingBottom: '10px', width: '100%', textAlign: 'center' }}>{t('boreholes.ruler_view', { defaultValue: 'القطاع الهندسي (المسطرة)' })}</h3>
            
            <div style={{ 
                position: 'relative',
                width: '180px',
                minHeight: '360px',
                border: '2px solid #555',
                borderRadius: '8px',
                overflow: 'hidden',
                backgroundColor: '#111',
                marginTop: '10px',
                // Start tracking total height based on layers
            }}>
                
                 <div style={{ position: 'absolute', top: '-10px', right: isRTL ? '-50px' : 'auto', left: isRTL ? 'auto' : '-50px', fontSize: '12px', color: '#ccc' }}>0.0m</div>

                {layers.length > 0 ? layers.map((layer, idx) => {
                    // Constant scale multiplier
                    const pxPerMeter = 10;
                    const thickness = getLayerThickness(layer);
                    const height = Math.max(40, thickness * pxPerMeter);
                    const bgColor = getLayerColor(layer.legend || layer.layer_description);

                    return (
                        <div key={idx} style={{ 
                            width: '100%', 
                            height: `${height}px`, 
                            backgroundColor: bgColor,
                            borderBottom: idx === layers.length - 1 ? 'none' : '1px solid rgba(0,0,0,0.3)',
                            position: 'relative',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                        }}>
                             <span style={{ 
                                color: '#111', 
                                fontWeight: 'bold', 
                                textShadow: '0px 0px 2px rgba(255,255,255,0.7)',
                                fontSize: '14px',
                                textAlign: 'center',
                                padding: '0 5px'
                            }}>
                                {`${getDisplayValue(layer.legend || layer.layer_description).substring(0, 18)}...`}
                            </span>
                            
                           
                            <div style={{ 
                                position: 'absolute', 
                                bottom: '-8px',
                                right: isRTL ? '-50px' : 'auto', 
                                left: isRTL ? 'auto' : '-50px', 
                                fontSize: '12px', 
                                color: '#ccc',
                                borderTop: '1px solid #777',
                                width: '40px',
                                textAlign: isRTL ? 'left' : 'right',
                                padding: '0 4px'
                            }}>
                                {`${getDisplayValue(layer.end_depth, '0')}m`}
                            </div>
                        </div>
                    );
                }) : (
                    <div style={{ padding: '50px 20px', textAlign: 'center', color: '#aaa' }}>{t('noData', { defaultValue: 'لا توجد طبقات' })}</div>
                )}
            </div>
        </div> */}

        {/* Data Table Sector */}
        <div style={{ flex: '2', minWidth: '400px', backgroundColor: '#1b3350', borderRadius: '12px', border: '1px solid #333', padding: '20px' }}>
            <h3 style={{ marginBottom: '20px', borderBottom: '1px solid #333', paddingBottom: '10px' }}>{t('boreholes.layers_data', { defaultValue: 'بيانات الطبقات (الجدول)' })}</h3>
            
            <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'center' }}>
                    <thead>
                        <tr style={{ backgroundColor: '#2f3644' }}>
                            <th style={{ padding: '10px', border: '1px solid #444' }}>{t('table.number', { defaultValue: 'م' })}</th>
                            <th style={{ padding: '10px', border: '1px solid #444' }}>{t('boreholes.start_depth', { defaultValue: 'من (م)' })}</th>
                            <th style={{ padding: '10px', border: '1px solid #444' }}>{t('boreholes.end_depth', { defaultValue: 'إلى (م)' })}</th>
                            <th style={{ padding: '10px', border: '1px solid #444' }}>{t('boreholes.thickness', { defaultValue: 'السماكة (م)' })}</th>
                            <th style={{ padding: '10px', border: '1px solid #444' }}>{t('boreholes.legend', { defaultValue: 'المصطلح (Legend)' })}</th>
                            <th style={{ padding: '10px', border: '1px solid #444' }}>{t('boreholes.layer_description', { defaultValue: 'الوصف التفصيلي' })}</th>
                            <th style={{ padding: '10px', border: '1px solid #444' }}>SPTs</th>
                            <th style={{ padding: '10px', border: '1px solid #444' }}>RQD%</th>
                            <th style={{ padding: '10px', border: '1px solid #444' }}>TCR%</th>
                            <th style={{ padding: '10px', border: '1px solid #444' }}>UCS</th>
                        </tr>
                    </thead>
                    <tbody>
                        {layers.length > 0 ? layers.map((layer, idx) => (
                            <tr key={idx}>
                                <td style={{ padding: '10px', border: '1px solid #444' }}>{idx + 1}</td>
                                <td style={{ padding: '10px', border: '1px solid #444', color: '#AC2000' }}>{getDisplayValue(layer.start_depth)}</td>
                                <td style={{ padding: '10px', border: '1px solid #444', color: '#AC2000' }}>{getDisplayValue(layer.end_depth)}</td>
                                <td style={{ padding: '10px', border: '1px solid #444', fontWeight: 'bold' }}>{getDisplayValue(layer.thickness || getLayerThickness(layer))}</td>
                                <td style={{ padding: '10px', border: '1px solid #444' }}>{getDisplayValue(layer.legend || layer.layer_description)}</td>
                                <td style={{ padding: '10px', border: '1px solid #444' }}>{getDisplayValue(layer.layer_description)}</td>
                                <td style={{ padding: '10px', border: '1px solid #444' }}>{getDisplayValue(layer.spt_n)}</td>
                                <td style={{ padding: '10px', border: '1px solid #444' }}>{getDisplayValue(layer.rqd_perc)}</td>
                                <td style={{ padding: '10px', border: '1px solid #444' }}>{getDisplayValue(layer.tcr_perc)}</td>
                                <td style={{ padding: '10px', border: '1px solid #444' }}>{getDisplayValue(layer.ucs_pn)}</td>
                            </tr>
                        )) : (
                            <tr><td colSpan={10} style={{ padding: '15px' }}>{t('noData', { defaultValue: 'لا توجد بيانات' })}</td></tr>
                        )}
                    </tbody>
                </table>
            </div>
        </div>

      </div>
    </div>
  );
};

export default BoreholeDetails;
